import {
  CalendarBlank,
  CaretUpDown,
  Check,
  ClockCounterClockwise,
  Gauge,
  House,
  PaperPlaneTilt,
  type Icon as PhIcon,
  SignOut,
  SlidersHorizontal,
  Users,
} from "@phosphor-icons/react";
import { Link, useNavigate, useRouterState } from "@tanstack/react-router";
import { type ReactNode, useLayoutEffect, useRef } from "react";
import { Av, Ic } from "@/components/bits";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { EVENT } from "@/lib/fields";
import { SENDER_BADGE, senderState } from "@/model/advice";
import { doSignOut, useUser } from "./AuthGate";
import { useApp } from "./appData";

type To = "/" | "/wa" | "/people" | "/send" | "/history" | "/settings";

function NavItem({
  to,
  icon,
  label,
  on,
  extra,
}: {
  to: To;
  icon: PhIcon;
  label: string;
  on: boolean;
  extra?: ReactNode;
}) {
  return (
    <Link to={to} className={`nav${on ? " on" : ""}`} aria-current={on ? "page" : undefined}>
      <Ic i={icon} w={on ? "fill" : "regular"} />
      <span className="lbl">{label}</span>
      {extra}
    </Link>
  );
}

export function Sidebar({ open }: { open: boolean }) {
  const path = useRouterState({ select: (s) => s.location.pathname });
  const { counts, cap, runs, next } = useApp();
  const user = useUser();
  const navigate = useNavigate();
  const st = senderState(runs, cap, next.waiting, Date.now(), counts.people);
  const body = useRef<HTMLElement>(null);
  const ind = useRef<HTMLSpanElement>(null);
  const lastY = useRef<number | null>(null);

  // Glide the primary-soft indicator to the active item (prototype glideNav).
  // biome-ignore lint/correctness/useExhaustiveDependencies: re-run when the route changes
  useLayoutEffect(() => {
    const el = ind.current;
    const on = body.current?.querySelector<HTMLElement>(".nav.on");
    if (!el) return;
    if (!on) {
      el.style.opacity = "0";
      lastY.current = null;
      return;
    }
    const y = on.offsetTop;
    el.style.opacity = "1";
    el.style.transition = "none";
    el.style.transform = `translateY(${lastY.current ?? y}px)`;
    void el.offsetWidth;
    el.style.transition = "";
    el.style.transform = `translateY(${y}px)`;
    lastY.current = y;
  }, [path]);

  const name = user.displayName || user.email?.split("@")[0] || "Admin";

  return (
    <aside className={`sidebar${open ? " on" : ""}`} aria-label="Main navigation">
      <div className="sb-head">
        <div className="brand">
          <span className="logo">AI</span>
          <div style={{ minWidth: 0 }}>
            <b>AI Conference Twin</b>
            <span>{EVENT.org}</span>
          </div>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button type="button" className="evswitch">
              <div className="t">
                <b>{EVENT.name}</b>
                <span>{EVENT.dates}</span>
              </div>
              <Ic i={CaretUpDown} className="muted" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="pop rdx" style={{ minWidth: 232 }}>
            <DropdownMenuLabel className="pl">{EVENT.org}</DropdownMenuLabel>
            <DropdownMenuItem onSelect={() => navigate({ to: "/wa" })}>
              <Ic i={CalendarBlank} />
              {EVENT.name}
              <span className="s">
                <Ic i={Check} />
              </span>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => navigate({ to: "/" })}>
              <Ic i={House} />
              All events
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <nav className="sb-body" ref={body}>
        <span className="nav-ind" ref={ind} />
        <div className="sb-label">Workspace</div>
        <NavItem to="/" icon={House} label="All events" on={path === "/"} />
        <div className="sb-label">WhatsApp</div>
        <NavItem to="/wa" icon={Gauge} label="Overview" on={path === "/wa"} />
        <NavItem
          to="/people"
          icon={Users}
          label="People & replies"
          on={path === "/people"}
          extra={
            counts.waiting ? (
              <span className="cnt hot" data-testid="nav-waiting">
                {counts.waiting}
              </span>
            ) : null
          }
        />
        <NavItem
          to="/send"
          icon={PaperPlaneTilt}
          label="Send"
          on={path === "/send"}
          extra={
            <span
              className={`sdot ${SENDER_BADGE[st].cls}`}
              style={{ width: 7, height: 7 }}
              title={SENDER_BADGE[st].label}
            />
          }
        />
        <NavItem to="/history" icon={ClockCounterClockwise} label="Send history" on={path === "/history"} />
        <NavItem to="/settings" icon={SlidersHorizontal} label="Settings" on={path === "/settings"} />
      </nav>
      <div className="sb-foot">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button type="button" className="user">
              <Av name={name} />
              <div className="t">
                <b>{name}</b>
                <span>Admin · {user.email}</span>
              </div>
              <Ic i={CaretUpDown} className="muted" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent side="top" align="start" className="pop rdx" style={{ minWidth: 232 }}>
            <DropdownMenuLabel className="pl">{user.email}</DropdownMenuLabel>
            <DropdownMenuItem onSelect={() => doSignOut()}>
              <Ic i={SignOut} />
              Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </aside>
  );
}
